import { useState, useEffect } from "react";
import { Crosshair, Check } from "lucide-react";

const BUCKETS = [
  { key: "0", label: "0 Runs", sub: "Maiden", testid: "predict-bucket-0", accent: "bg-slate-800/60 border-slate-700 text-slate-200" },
  { key: "1-3", label: "1–3", sub: "Rotate", testid: "predict-bucket-1-3", accent: "bg-sky-500/10 border-sky-500/30 text-sky-200" },
  { key: "4-6", label: "4–6", sub: "Boundary", testid: "predict-bucket-4-6", accent: "bg-amber-500/10 border-amber-500/30 text-amber-200" },
  { key: "7+", label: "7+", sub: "Carnage", testid: "predict-bucket-7-plus", accent: "bg-purple-500/10 border-purple-500/30 text-purple-200" },
];

export default function OverPredictionForm({ match, upcomingOver, existing, onSubmit }) {
  const [bucket, setBucket] = useState(null);
  const [wicket, setWicket] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (existing) {
      setBucket(existing.runs_bucket);
      setWicket(existing.wicket_prediction);
    } else {
      setBucket(null);
      setWicket(null);
    }
  }, [existing, upcomingOver]);

  const locked = Boolean(existing);

  const submit = async () => {
    if (!bucket || wicket === null) return;
    setBusy(true);
    try {
      await onSubmit({
        match_id: match.id,
        over_number: upcomingOver,
        runs_bucket: bucket,
        wicket_prediction: wicket,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tile p-6 grain">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Crosshair className="w-4 h-4 text-amber-400" />
          <div className="section-label">Your call</div>
        </div>
        <div className="font-mono-data text-amber-400 text-lg">Over #{upcomingOver}</div>
      </div>

      {locked && (
        <div className="pill bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 mb-4">
          <Check className="w-3.5 h-3.5" /> Locked · awaiting result
        </div>
      )}

      <div className="section-label mb-2">Runs bucket</div>
      <div className="grid grid-cols-2 gap-2 mb-5">
        {BUCKETS.map((b) => (
          <button
            key={b.key}
            disabled={locked}
            onClick={() => setBucket(b.key)}
            className={`p-3 rounded-lg border text-left transition-all ${b.accent} ${bucket === b.key ? "ring-2 ring-amber-400 border-amber-400 scale-[1.02]" : "opacity-80 hover:opacity-100"} ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
            data-testid={b.testid}
          >
            <div className="font-display font-black text-2xl uppercase">{b.label}</div>
            <div className="text-xs uppercase tracking-wider">{b.sub}</div>
          </button>
        ))}
      </div>

      <div className="section-label mb-2">Wicket next over?</div>
      <div className="grid grid-cols-2 gap-2 mb-5">
        <button
          disabled={locked}
          onClick={() => setWicket(true)}
          className={`p-3 rounded-lg border transition-all bg-rose-500/10 border-rose-500/30 text-rose-200 ${wicket === true ? "ring-2 ring-rose-400 scale-[1.02]" : "opacity-80 hover:opacity-100"} ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
          data-testid="predict-wicket-yes"
        >
          <div className="font-display font-black text-lg uppercase">Yes · W</div>
        </button>
        <button
          disabled={locked}
          onClick={() => setWicket(false)}
          className={`p-3 rounded-lg border transition-all bg-emerald-500/10 border-emerald-500/30 text-emerald-200 ${wicket === false ? "ring-2 ring-emerald-400 scale-[1.02]" : "opacity-80 hover:opacity-100"} ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
          data-testid="predict-wicket-no"
        >
          <div className="font-display font-black text-lg uppercase">No</div>
        </button>
      </div>

      <button
        onClick={submit}
        disabled={locked || busy || !bucket || wicket === null}
        className="btn-primary w-full"
        data-testid="submit-prediction-button"
      >
        {locked ? "Locked in" : busy ? "Locking…" : "Lock prediction"}
      </button>

      <div className="mt-4 text-[11px] text-slate-500 leading-relaxed">
        Runs correct: <span className="text-emerald-400">+10</span> · Wicket correct: <span className="text-emerald-400">+8</span> · Combo bonus: <span className="text-amber-400">+12</span>
      </div>
    </div>
  );
}
